/**
 * Button that fetches and displays Time Analysis charts for an AIMD job.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */
import { downloader } from '@/config/downloader'
import { logger, LogTag } from '@/config/logger'
import TimeAnalysisDialog, {
  TimeAnalysisSeries,
} from '@/pages/TaskList/TimeAnalysisChart'
import { JobDetail } from '@amplify3/duplo-back-end'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useState } from 'react'
import { BiLineChart } from 'react-icons/bi'
import { IconType } from 'react-icons/lib'
import ProgressIconButton from './ProgressIconButton'

/** One time-analysis entry extracted from a job's report. */
interface TimeAnalysis {
  timeAnalysisIasId: string
  inputFileName: string
  isAi: boolean
  /** Raw JSON data, populated lazily on first chart open. */
  data?: any
}

/** Cached job details held in local state to avoid re-fetching. */
interface JobResult {
  jobId: string
  report: any
  fileList: any[]
  timeAnalysisId: any
  timeAnalysisList: TimeAnalysis[] | undefined
}

/** Props for {@link AimdTimeAnalysisButton}. */
interface TimeAnalysisHandlerProps {
  jobResult: JobDetail
  label?: string
  icon?: IconType
  /** If set, only the analysis at this index is fetched and displayed. */
  inputFileIndex?: number
  /** When true, hides the file-selector dropdown and shows only one chart. */
  showOnlyOneAnalysis?: boolean
}

/**
 * Button that lazily downloads time-analysis data from IAS and opens
 * a chart dialog. Data is cached in local state after the first fetch.
 */
const AimdTimeAnalysisButton = (props: TimeAnalysisHandlerProps) => {
  const [isLoadingCharts, setLoadingCharts] = useState(false)
  const [isChartsDialogVisible, setChartsDialogVisible] = useState(false)
  const [jobResult, setJobResult] = useState<JobResult | undefined>(undefined)

  const [timeAnalysisSeries, setTimeAnalysisSeries] = useState<
    TimeAnalysisSeries[]
  >([])

  function showChartsDialogVisible(series: TimeAnalysisSeries[]) {
    setTimeAnalysisSeries(series)
    setChartsDialogVisible(true)
  }

  /** Reads the job report and builds the list of time-analysis entries. */
  async function getJobResult(resultIndex?: number) {
    const data = props.jobResult

    const report = data?.report_info?.report ?? data?.report_info
    logger.debug(LogTag.TaskList, 'report:', report)

    const fileList = data?.file_paths ?? []
    logger.debug(LogTag.TaskList, 'filePaths:', fileList)

    // Get list of Time Analysis
    const timeAnalysisList: TimeAnalysis[] = []

    if (report?.resultList) {
      for (let index = 0; index < report.resultList.length; ++index) {
        const currentResult = report.resultList.at(index)
        if (currentResult.timeAnalysis) {
          const item = {
            timeAnalysisIasId: currentResult.timeAnalysis.id,
            inputFileName: currentResult.inputFilename,
            isAi: currentResult.isAi,
          }
          timeAnalysisList.push(item)
        }
      }
    }

    const result = {
      jobId: props.jobResult.job_id,
      report: report,
      fileList: fileList,
      timeAnalysisId: report?.resultList?.at(resultIndex ?? 0)?.timeAnalysis,
      timeAnalysisList: timeAnalysisList,
    }
    setJobResult(result)

    return result
  }

  /**
   * Handler of the click on the link to show the Time Analysis charts.
   */
  const onViewChartClick = async () => {
    setLoadingCharts(true)
    logger.debug(LogTag.TaskList, 'onViewChartClick')

    if (timeAnalysisSeries?.length > 0) {
      // Time analysis files already downloaded: show the dialog
      showChartsDialogVisible(timeAnalysisSeries)
    } else {
      // Time analysis files not ready yet: download them from the back-end
      const result = jobResult ?? (await getJobResult(props.inputFileIndex))

      // Get data if needed
      if (result.timeAnalysisList && result.timeAnalysisList.length > 0) {
        let resultChangeCount = 0

        const listToProcess =
          props.inputFileIndex !== undefined
            ? [result.timeAnalysisList[props.inputFileIndex]]
            : result.timeAnalysisList

        // Process all data checking in parallel
        await Promise.all(
          listToProcess.map(async (analysis: TimeAnalysis) => {
            if (undefined == analysis.data) {
              // Data not loaded yet: download them from the back-end
              const session = await fetchAuthSession()
              const analysisText = await downloader.asyncGetContentFromIas(
                session,
                analysis.timeAnalysisIasId
              )
              const json = JSON.parse(analysisText)
              ++resultChangeCount
              analysis.data = json
            }
          })
        )

        logger.debug(LogTag.TaskList, 'onViewChartClick result:', result)
        if (resultChangeCount > 0) {
          setJobResult(result)
        }

        // Create chart series
        const seriesList: TimeAnalysisSeries[] = []
        for (const timeData of listToProcess) {
          const series = new TimeAnalysisSeries(
            timeData.data?.timestamps,
            timeData.data?.aiProbability,
            timeData.inputFileName
          )
          seriesList.push(series)
        }
        setTimeAnalysisSeries(seriesList)
        showChartsDialogVisible(seriesList)
      }
    }

    setLoadingCharts(false)
  }

  return (
    <div>
      <ProgressIconButton
        className="aimd-time-analysis"
        onClick={onViewChartClick}
        isWaiting={isLoadingCharts}
        isWaitingShowlabel={true}
        icon={props.icon ?? BiLineChart}
        label={props.label}
      />

      {timeAnalysisSeries.length > 0 && isChartsDialogVisible && (
        <TimeAnalysisDialog
          currentSeriesIndex={
            props.showOnlyOneAnalysis ? 0 : props.inputFileIndex
          }
          showOnlyOneAnalysis={props.showOnlyOneAnalysis}
          series={timeAnalysisSeries}
          changeVisible={setChartsDialogVisible}
          isVisible={isChartsDialogVisible}
        />
      )}
    </div>
  )
}

export default AimdTimeAnalysisButton
